// Application Components
import React, { useState, useMemo, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import axios from 'axios'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import { selectAccount } from '../../reducers/accountSlice'
import {
    updateFormData,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import SubmissionStructuredListComponent from '../../components/conditional-components/submission-structured-list-component'
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'

//// Carbon Components

//// Carbon Icons

//// Static Constants

//////// Component
const SummarySubmissionForm = ({ instance }) => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const accountData = useSelector(selectAccount)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const category_digital_name =
        settingsData?.['form']?.['category_digital_name']
    const max_form_iterations =
        settingsData?.['application']?.['max_form_iterations']
    const geo_codes = settingsData?.['application']?.['geo_codes']
    const configuration =
        settingsData?.configuration?.forms?.[category_digital_name]

    //////// Search Params

    //////// Methods

    // Orchestrate Login Data (Integration)
    const orchestrateApplicationSubmission = async (
        xact_cde,
        xact_label,
        clnt_cde,
        reguserid,
        tax_nbr,
        forms
    ) => {
        try {
            if (xact_cde && xact_label && clnt_cde && reguserid && forms) {
                const base_url = `${process.env.REACT_APP_BASE_URL}`
                const api_endpoint = `${process.env.REACT_APP_ORCHESTRATION_ENDPOINT_PROXY_APPLICATION_SUBMISSION}`
                const url = base_url + api_endpoint

                const { data } = await axios.post(url, {
                    xact_cde: xact_cde,
                    xact_label: xact_label,
                    clnt_cde: clnt_cde,
                    reguserid: reguserid,
                    tax_nbr: tax_nbr,
                    forms: forms,
                })
                if (data) {
                    return data
                } else {
                    console.log(`Account Login: Response Is Undefined`)
                    return null
                }
            } else {
                console.log(`Account Login: Request Is Undefined`)
                return null
            }
        } catch (error) {
            console.warn(`Orchestrate Application Submission Error:`, error)
            return null
        }
    }

    // Translate Field Key To Plain Text (Multi-Form [Summary Submission Form])
    const translateKey = (fields, field) => {
        if (fields && field) {
            // Check In Result Variable
            const entry = {}

            // Check For Initial Iterative Form
            entry.converted = fields[field]

            // Check For Iterative Form (0-9)
            if (!entry?.['converted']) {
                // Extract Last Character Of Array
                const lastVarChar = field?.slice(-1).toString()

                // Check For Characters In Max Form Iterations
                const number = max_form_iterations?.indexOf(lastVarChar)

                // Check For Index Of VarChar In Max Form Interactions
                if (number > -1) {
                    // Clean Key From Appended Number
                    const fldArr = Array.from(field)
                    const last = fldArr.length - 1
                    const newArr = fldArr.splice(0, last)
                    entry.clean_key = newArr.join('')

                    // Check For Clean Key
                    if (entry?.['clean_key']) {
                        // Check Fields For Field
                        const convertedExt = fields?.[entry?.clean_key]

                        // Check For Converted Extended
                        if (convertedExt) {
                            entry.converted = convertedExt
                        }
                    }
                }
            }

            // Check For Iterative Form (10 - 50)
            if (!entry?.['converted']) {
                // Extract Last Two Characters Of Array
                const lastTwoVarChar = field?.slice(-2).toString()

                // Check For Characters In Max Form Iterations
                const number = max_form_iterations?.indexOf(lastTwoVarChar)

                // Check For Index Of VarChar In Max Form Interactions
                if (number > -1) {
                    // Clean Key From Appended Number
                    const fldArr = Array.from(field)
                    const lastTwo = fldArr.length - 2
                    const newArr = fldArr.splice(0, lastTwo)
                    entry.clean_key = newArr.join('')

                    // Check For Clean Key
                    if (entry?.['clean_key']) {
                        // Check Fields For Field
                        const convertedExt = fields?.[entry?.clean_key]

                        // Check For Converted Extended
                        if (convertedExt) {
                            entry.converted = convertedExt
                        }
                    }
                }
            }

            // Check For Clean Key
            if (!entry?.['clean_key'] && entry.converted) {
                entry.clean_key = entry.converted
            }

            // Check For Converted Entries
            if (entry?.['converted']) {
                return {
                    translated: true,
                    key: entry?.clean_key,
                    field: entry?.converted,
                }
            } else {
                return {
                    translated: false,
                    key: entry?.clean_key,
                    field: field,
                }
            }
        }
    }

    // Translate Field Value To Plain Text (Multi-Form [Summary Submission Form])
    const translateValue = (fields, inputs, key, input) => {
        if (fields && inputs && key && input) {
            // Check In Result Variable
            const entry = {}

            // Check Inputs For Config With Key
            entry.key_config = inputs?.[key]

            if (!entry?.['key_config']) {
                const keyData = translateKey(fields, key)
                if (keyData?.['translated'] && keyData?.['key']) {
                    entry.key_config = inputs?.[keyData?.key]
                }
            }

            // Check For 1:1 Translation
            if (
                !entry?.['converted'] &&
                typeof entry?.['key_config'] === 'object'
            ) {
                // Check Config With Value
                const valueConverted = entry?.['key_config']?.[input]

                // Check For Value
                if (valueConverted) {
                    entry.converted = valueConverted
                }
            }

            // Check For Application Settings Translation
            if (
                !entry?.['converted'] &&
                typeof entry?.['key_config'] === 'string'
            ) {
                // Check Geo Code Config For Key
                const geoCodeConfig = geo_codes?.[entry?.['key_config']]
                if (geoCodeConfig) {
                    // Check Config With Value
                    const valueConverted = geoCodeConfig?.[input]

                    // Check For Value
                    if (valueConverted) {
                        entry.converted = valueConverted
                    }
                }
            }

            // Check For Converted Entry
            if (entry?.['converted']) {
                return { translated: true, input: entry?.converted }
            } else {
                return { translated: false, input: input }
            }
        } else {
            return null
        }
    }

    const structureSummarize = (submission_keys, submission_values) => {
        // Check In Submission Object
        const submission = {}

        // Create Plain Text Form Submission Object
        if (
            submission_keys?.length > 0 &&
            submission_values?.length > 0 &&
            submission_keys?.length === submission_values?.length
        ) {
            // Check In Result Variables
            const usedKeysNotations = {}

            // Iterate Over Submission Keys To Re-Create Entry Object
            submission_keys.forEach((el, i) => {
                // Check In Key And Value Variables
                const key = el
                const value = submission_values?.[i]

                // Create Array Of Used Key Notations
                const usedKeyNotationsArr = Object?.keys(usedKeysNotations)

                // Test For Existing Key
                const keyTest = usedKeyNotationsArr?.includes(key)

                // Check If Key Exists In Object
                if (keyTest === false && key && value) {
                    usedKeysNotations[key] = { count: 1 }
                    submission[key] = value
                } else {
                    // Extract Key Object For Count
                    const obj = usedKeysNotations?.[key]

                    // Check For Key Object
                    if (obj) {
                        const count = obj?.count
                        const newCount = count + 1
                        usedKeysNotations[key]['count'] = newCount
                        submission[key + ' ' + '(' + newCount + ')'] = value
                    } else {
                        usedKeysNotations[key] = {
                            count: 1,
                        }
                        submission[key] = value
                    }
                }
            })
        }

        return submission
    }

    // Finalize Individual Form Submission Data
    const translateSummarize = (config, entries) => {
        const submissionKeys = []
        const submissionValues = []

        // Check Form Configuration For Fields (Convert Keys Of Values To Fields)
        if (config?.['fields'] && entries) {
            // Array Of Submitted Names
            const submittedKeys = Object?.keys(entries)

            // Array Of Field Values
            const submittedValues = Object?.values(entries)

            // Check For Entries In Configuration
            if (submittedKeys?.length > 0 && submittedValues?.length > 0) {
                submittedKeys.forEach((el) => {
                    const keyData = translateKey(config?.fields, el)
                    if (keyData?.['translated'] && keyData?.['field']) {
                        submissionKeys.push(keyData?.field)
                    } else {
                        submissionKeys.push(el)
                    }
                })
            }
        }
        // Check Form Configuration For Inputs (Convert Values Of Values To Inputs)
        if (config?.['inputs'] && entries) {
            // Array Of Field Values
            const submittedValues = Object?.values(entries)

            // Array Of Submitted Names
            const submittedKeys = Object?.keys(entries)

            // Check For Config In Configuration
            if (submittedValues?.length > 0 && submittedKeys?.length > 0) {
                submittedValues.forEach((el, i) => {
                    const valueData = translateValue(
                        config?.fields,
                        config?.inputs,
                        submittedKeys?.[i],
                        el
                    )
                    if (valueData?.['translated'] && valueData?.['input']) {
                        submissionValues.push(valueData?.input)
                    } else {
                        submissionValues.push(el)
                    }
                })
            }
        }

        return {
            submission_keys: submissionKeys,
            submission_values: submissionValues,
        }
    }

    // Finalize Form Submission Data
    const summarize = (formsData) => {
        if (formsData) {
            // Check In Submission Object
            const submissions = []

            // Array Of Form Names
            const fieldNames = Object?.keys(formsData)
            delete fieldNames?.['summary-submission-form']
            delete fieldNames?.['file-attachments']
            delete fieldNames?.['fee-and-summary-report']

            // Array Of Form Objects
            const formObjects = Object?.values(formsData)
            delete formObjects?.['summary-submission-form']
            delete formObjects?.['file-attachments']
            delete formObjects?.['fee-and-summary-report']

            // Check Length Of Form Names & Form Objects
            if (fieldNames?.length > 0 && formObjects?.length > 0) {
                // Iterate Over Form Objects Array
                formObjects.forEach((el, i) => {
                    // Extract Form From Object index
                    const digital_name = fieldNames?.[i]

                    // Check For Digital Name
                    if (
                        digital_name &&
                        digital_name !== 'summary-submission-form' &&
                        digital_name !== 'file-attachments' &&
                        digital_name !== 'fee-and-summary-report'
                    ) {
                        // Extract Entry Values Array From Form
                        const entries = formObjects?.[i]?.['values']

                        // Check For Digital Name And Form Entries
                        if (digital_name && entries) {
                            // Extract Configuration Object From Form Value For Structuring
                            const config =
                                settingsData?.configuration?.forms?.[
                                    category_digital_name
                                ]?.[digital_name]

                            const { submission_keys, submission_values } =
                                translateSummarize(config, entries)

                            const submission = structureSummarize(
                                submission_keys,
                                submission_values
                            )

                            // Check For Plain Text Form Submission Object
                            if (submission) {
                                submissions.push({
                                    written_name: config?.written_name,
                                    route:
                                        config?.category_route +
                                        config?.current_route,
                                    summary: submission,
                                })
                            } else {
                                submissions.push({
                                    written_name: config?.written_name,
                                    route:
                                        config?.category_route +
                                        config?.current_route,
                                    summary: entries,
                                })
                            }
                        } else if (digital_name) {
                            // Extract Form Object
                            const formObject = formObjects?.[i]

                            // Check For Form Object
                            if (formObject) {
                                // Extract Configuration Object From Form Value For Structuring
                                const config =
                                    settingsData?.configuration?.forms?.[
                                        category_digital_name
                                    ]?.[digital_name]

                                // Check For Configuration
                                if (config) {
                                    const subForms = config?.sub_forms
                                    const subFormsNames =
                                        config?.sub_forms_written_names

                                    // Check Form Configuration For SubForms (Convert Values Of Values To Inputs)
                                    if (subForms && subForms?.length > 0) {
                                        config?.sub_forms?.forEach(
                                            (subFormName, i) => {
                                                const subFormObject =
                                                    formObject?.[subFormName]
                                                if (subFormObject) {
                                                    // Extract Entry Values Array From Form
                                                    const subEntries =
                                                        subFormObject?.values

                                                    // Check For Form Entries
                                                    if (subEntries) {
                                                        const {
                                                            submission_keys,
                                                            submission_values,
                                                        } = translateSummarize(
                                                            config,
                                                            subEntries
                                                        )

                                                        const submission =
                                                            structureSummarize(
                                                                submission_keys,
                                                                submission_values
                                                            )

                                                        // Check For Plain Text Form Submission Object
                                                        if (submission) {
                                                            submissions.push({
                                                                written_name:
                                                                    config?.written_name +
                                                                    ' ' +
                                                                    subFormsNames?.[
                                                                        i
                                                                    ],
                                                                route:
                                                                    config?.category_route +
                                                                    config?.current_route,
                                                                summary:
                                                                    submission,
                                                            })
                                                        } else {
                                                            submissions.push({
                                                                written_name:
                                                                    config?.written_name +
                                                                    ' ' +
                                                                    subFormsNames?.[
                                                                        i
                                                                    ],
                                                                route:
                                                                    config?.category_route +
                                                                    config?.current_route,
                                                                summary:
                                                                    entries,
                                                            })
                                                        }
                                                    }
                                                }
                                            }
                                        )
                                    }
                                }
                            }
                        }
                    }
                })
            }

            return submissions
        } else {
            return null
        }
    }

    // Extract Form Data Objects
    const extract = () => {
        // Check In Operation Variables
        const extraction = {}

        // Check For Settings Data To Extract
        if (
            settingsData?.['category']?.['xact_cde'] &&
            settingsData?.['category']?.['xact_label'] &&
            settingsData?.['category']?.['clnt_cde']
        ) {
            extraction.xact_cde = settingsData?.category?.xact_cde
            extraction.xact_label = settingsData?.category?.xact_label
            extraction.clnt_cde = settingsData?.category?.clnt_cde
        }

        // Check For Account Data To Extract
        if (accountData?.['login']?.values?.['reguserid']) {
            extraction.reguserid = accountData?.login?.values?.reguserid
        }

        // Check For Forms Data To Extract
        if (formsData) {
            extraction.tax_nbr =
                formsData?.['name-and-personal-details']?.values?.tax_nbr
            extraction.forms = formsData
        }

        return extraction
    }

    // Validate Forms
    const validation = async (extracted) => {
        try {
            if (extracted?.['forms']) {
                const forms = extracted?.forms
                if (forms) {
                    const formNames = Object?.keys(forms)
                    if (formNames) {
                        // Check In Operation Variabless
                        const validatedFormNames = []
                        const unvalidatedFormNames = []
                        const unvalidatedFormSpecificFormNames = []
                        const dynamicRequiredFormNames = []
                        const dynamicNonRequiredFormNames = []
                        const summarizedFormNames = []
                        const summarizedFormSpecificFormNames = []
                        const translatedSummarizedFormNames = []
                        const validity = {}

                        ////* Check For All Forms Being Validated *////

                        // Check For Forms
                        if (formNames?.length > 0) {
                            // Iterate Over Forms To Assure Validation
                            formNames?.forEach((formName) => {
                                const nonRequired = configuration?.non_required
                                if (nonRequired) {
                                    const systemFormIndex =
                                        nonRequired?.indexOf(formName)
                                    if (systemFormIndex === -1) {
                                        const system = forms?.[formName]?.system
                                        if (system) {
                                            const valid = system?.valid
                                            if (valid) {
                                                validatedFormNames.push(
                                                    formName
                                                )
                                            } else {
                                                unvalidatedFormNames.push(
                                                    formName
                                                )
                                            }
                                        } else {
                                            unvalidatedFormNames.push(formName)
                                        }
                                    } else {
                                        // Skip Validation For System Forms
                                        validatedFormNames.push(formName)
                                    }
                                } else {
                                    unvalidatedFormNames.push(formName)
                                }
                            })
                        }

                        ////* Check For Dynamic And Not Required Forms *////

                        // Check For Unvalidated Forms
                        if (unvalidatedFormNames?.length > 0) {
                            // Iterate Over Forms To Remove Dynamic Non-Required
                            unvalidatedFormNames.forEach((formName) => {
                                const dynamic = configuration?.dynamic
                                if (dynamic) {
                                    const dynamicForms = Object?.keys(dynamic)
                                    if (dynamicForms?.length > 0) {
                                        const dynamicFormIndex =
                                            dynamicForms?.indexOf(formName)
                                        if (dynamicFormIndex > -1) {
                                            // Extract Name Of Dynamic Form
                                            const dynamicForm =
                                                dynamicForms?.[dynamicFormIndex]
                                            if (dynamicForm) {
                                                // Extract Configuration Of Dynamic Form
                                                const dynamicConfig =
                                                    dynamic[dynamicForm]
                                                if (
                                                    dynamicConfig?.[
                                                        'form-required'
                                                    ] &&
                                                    dynamicConfig?.[
                                                        'question_required'
                                                    ] &&
                                                    dynamicConfig?.[
                                                        'value_required'
                                                    ]
                                                ) {
                                                    // Extract Required Rules
                                                    const formRequired =
                                                        dynamicConfig?.[
                                                            'form-required'
                                                        ]
                                                    const questionRequired =
                                                        dynamicConfig?.[
                                                            'question_required'
                                                        ]
                                                    const valueRequired =
                                                        dynamicConfig?.[
                                                            'value_required'
                                                        ]
                                                    const requiredValue =
                                                        formsData?.[
                                                            formRequired
                                                        ]?.values?.[
                                                            questionRequired
                                                        ]

                                                    // Check That Required Value Matches Value Required To Pass
                                                    if (
                                                        requiredValue ===
                                                        valueRequired
                                                    ) {
                                                        dynamicRequiredFormNames.push(
                                                            formName
                                                        )
                                                    } else {
                                                        dynamicNonRequiredFormNames.push(
                                                            formName
                                                        )
                                                    }
                                                }
                                            }
                                        }
                                    }
                                }
                            })
                        }

                        ////* Clean Array Of Non-Required Dynamic Forms *////

                        // Check For Unvalidated Form Names
                        if (unvalidatedFormNames?.length > 0) {
                            unvalidatedFormNames?.forEach(
                                (unvalidatedFormName) => {
                                    const dynamicNonRequiredFormNameIndex =
                                        dynamicNonRequiredFormNames?.indexOf(
                                            unvalidatedFormName
                                        )

                                    // Check That Form Name Is Not Required
                                    if (
                                        dynamicNonRequiredFormNameIndex === -1
                                    ) {
                                        summarizedFormNames.push(
                                            unvalidatedFormName
                                        )
                                    }
                                }
                            )
                        }

                        ////* Clean Array/s Of Non-Form Specific Forms *////

                        // Check For Non Configured Application Forms (Unvalidated Form Names)
                        if (unvalidatedFormNames?.length > 0) {
                            unvalidatedFormNames?.forEach(
                                (unvalidatedFormName) => {
                                    const formConfig =
                                        configuration?.[unvalidatedFormName]
                                    if (formConfig) {
                                        unvalidatedFormSpecificFormNames.push(
                                            unvalidatedFormName
                                        )
                                    }
                                }
                            )
                        }

                        // Check For Non Configured Application Forms (Summarized Form Names)
                        if (summarizedFormNames?.length > 0) {
                            summarizedFormNames?.forEach(
                                (summarizedFormName) => {
                                    const formConfig =
                                        configuration?.[summarizedFormName]
                                    if (formConfig) {
                                        summarizedFormSpecificFormNames.push(
                                            summarizedFormName
                                        )
                                    }
                                }
                            )
                        }

                        ////* Translate Summary Array *////

                        // Check For Unvalidated Forms
                        if (summarizedFormSpecificFormNames?.length > 0) {
                            summarizedFormSpecificFormNames?.forEach(
                                (summarizedFormSpecificFormName) => {
                                    const formConfig =
                                        configuration?.[
                                            summarizedFormSpecificFormName
                                        ]
                                    if (formConfig) {
                                        const writtenName =
                                            formConfig?.written_name
                                        if (writtenName) {
                                            translatedSummarizedFormNames.push(
                                                writtenName
                                            )
                                        } else {
                                            translatedSummarizedFormNames.push(
                                                summarizedFormSpecificFormName
                                            )
                                        }
                                    } else {
                                        translatedSummarizedFormNames.push(
                                            summarizedFormSpecificFormName
                                        )
                                    }
                                }
                            )
                        }

                        ////* Create Unvalidated Summary Array *////

                        // Check For Translated Summary Form Names Array
                        if (translatedSummarizedFormNames?.length > 0) {
                            validity.unvalidated_forms_summary =
                                translatedSummarizedFormNames.join(', ')
                        }

                        // Check For Summarized Form Names Array
                        if (
                            !validity?.['unvalidated_forms_summary']?.length &&
                            summarizedFormSpecificFormNames?.length > 0
                        ) {
                            validity.unvalidated_forms_summary =
                                summarizedFormSpecificFormNames.join(', ')
                        }

                        // Check For Fallback Form Names Array
                        if (!validity?.['unvalidated_forms_summary']) {
                            validity.unvalidated_forms_summary =
                                translatedSummarizedFormNames
                        }

                        ////* Check For Outcome *////

                        // Check For Unvalidated Forms
                        if (unvalidatedFormSpecificFormNames?.length === 0) {
                            return {
                                valid: true,
                                validated: validatedFormNames,
                                unvalidated: unvalidatedFormSpecificFormNames,
                            }
                        } else if (
                            unvalidatedFormSpecificFormNames?.length !== 0 &&
                            validity?.['unvalidated_forms_summary']?.length > 0
                        ) {
                            return {
                                valid: false,
                                validated: validatedFormNames,
                                unvalidated:
                                    validity?.unvalidated_forms_summary,
                            }
                        } else {
                            return {
                                valid: false,
                                validated: validatedFormNames,
                                unvalidated: unvalidatedFormSpecificFormNames,
                            }
                        }
                    } else {
                        return {
                            valid: false,
                            validated: [],
                            unvalidated: [],
                        }
                    }
                } else {
                    return {
                        valid: false,
                        validated: [],
                        unvalidated: [],
                    }
                }
            } else {
                return {
                    valid: false,
                    validated: [],
                    unvalidated: [],
                }
            }
        } catch (error) {
            console.warn('Validation Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('Application Submission: Submitted')

            setLoading(true)

            // Extract Submission
            const extracted = extract()

            // Check If Extracted Data Has Proper Submission Properties
            if (
                extracted?.['xact_cde'] &&
                extracted?.['xact_label'] &&
                extracted?.['clnt_cde'] &&
                extracted?.['reguserid'] &&
                extracted?.['forms']
            ) {
                console.info('Application Submission: Passed Extract')

                // Validate Forms
                const valid = await validation(extracted)

                // Check If All Forms Have Required Validation Charecters
                if (valid?.['valid']) {
                    console.info('Application Submission: Passed Valid')

                    // Send Orchestration Request
                    const orchestrate = await orchestrateApplicationSubmission(
                        extracted?.xact_cde,
                        extracted?.xact_label,
                        extracted?.clnt_cde,
                        extracted?.reguserid,
                        extracted?.tax_nbr,
                        extracted?.forms
                    )

                    console.info('Application Submission: Passed Orchestrate')

                    // Check For Orchestrate Response And Application Response Data
                    if (
                        orchestrate?.['application_submitted'] &&
                        orchestrate?.['application']?.['applc_id'] &&
                        orchestrate?.['application']?.['app_sub_id'] &&
                        orchestrate?.['application']?.['lic_id']
                    ) {
                        console.info('Application Submission: Succcess')

                        dispatch(
                            updateFormSystem({
                                digital_name: digital_name,
                                system: { valid: true },
                            })
                        )
                        dispatch(
                            updateFormData({
                                digital_name: digital_name,
                                values: {
                                    applc_id:
                                        orchestrate?.application?.applc_id,
                                    app_sub_id:
                                        orchestrate?.application?.app_sub_id,
                                    lic_id: orchestrate?.application?.lic_id,
                                },
                            })
                        )
                        setLoading(false)
                        setNotification({
                            type: 'inline',
                            visible: false,
                            kind: '',
                            title: '',
                            subtitle: '',
                        })

                        navigate('/account/fee-and-summary-report')
                    } else if (
                        !orchestrate?.['application_submitted'] &&
                        !orchestrate?.['application']?.['applc_id'] &&
                        !orchestrate?.['application']?.['app_sub_id'] &&
                        !orchestrate?.['application']?.['lic_id'] &&
                        orchestrate?.['application'] ===
                            'Application not suitable'
                    ) {
                        console.info(
                            'Application Submission: Failed - Existing Application'
                        )

                        dispatch(
                            updateFormSystem({
                                digital_name: digital_name,
                                system: { valid: false },
                            })
                        )
                        setLoading(false)
                        setNotification({
                            type: 'inline',
                            visible: true,
                            kind: 'error',
                            title: 'Application Submission Failed:',
                            subtitle:
                                'You Have Already Submitted An Application',
                        })

                        console.log(
                            'Application Submission Failed: Existing Application'
                        )
                    } else {
                        console.info(
                            'Application Submission: Failed - Submission API Error'
                        )

                        dispatch(
                            updateFormSystem({
                                digital_name: digital_name,
                                system: { valid: false },
                            })
                        )
                        setLoading(false)
                        setNotification({
                            type: 'inline',
                            visible: true,
                            kind: 'error',
                            title: 'Application Submission Failed:',
                            subtitle: 'Submission Data Is Invalid',
                        })

                        console.log(
                            'Application Submission Failed: Submission API Error'
                        )
                    }
                } else {
                    console.info(
                        'Application Submission: Failed - Invalid Form Data'
                    )

                    dispatch(
                        updateFormSystem({
                            digital_name: digital_name,
                            system: { valid: false },
                        })
                    )
                    setLoading(false)
                    if (valid?.['unvalidated']?.length > 0) {
                        setNotification({
                            type: 'inline',
                            visible: true,
                            kind: 'error',
                            title: 'Form Errors:',
                            subtitle: `There are previous forms that need to be entered or updated. Please review these forms ${await valid?.unvalidated} to complete your application.`,
                        })
                    } else {
                        setNotification({
                            type: 'inline',
                            visible: true,
                            kind: 'error',
                            title: 'Form Errors:',
                            subtitle:
                                'There are previous forms that need to be entered or updated. Please review these forms to complete your application.',
                        })
                    }

                    console.log(
                        'Application Submission Failed Valid: Invalid Form Data'
                    )
                }
            } else {
                console.info(
                    'Application Submission: Failed - No Extracted Form Data'
                )

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Application Submission Failed:',
                    subtitle: 'Please Finalize All Forms And Try Resubmitting',
                })

                console.log(
                    'Application Submission Failed:  No Extracted Form Data'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    //////// Memoization
    const summarization = useMemo(() => summarize(formsData), [formsData])

    //////// Effects/Side Effects

    // Reload Watson Assistant Journey on Reload
    useEffect(() => {
        if (instance?.tours) {
            instance.tours.endTour()
            instance.tours.startTour(
                'Registered Nursing Endorsement Summary Submission Tour'
            )
        }
        return () => {}
    }, [instance])

    //////////////// Render & Return
    if (!loading && summarization?.length > 0) {
        return (
            <>
                <p className="emphasize">
                    The payment page will display upon submitting the
                    application. You may submit the fee now or return at a later
                    date, however, applications are not processed until the
                    payment has been received. To make a payment at a later
                    date, please log in to your MQA Online Services Portal
                    account and select “Make Payments” from the “Additional
                    Activities” menu. Use the same login credentials to log in
                    to the MQA Online Services Portal.
                </p>

                <div className="form-structured-list-group">
                    {summarization.map((el, i) => (
                        <SubmissionStructuredListComponent key={i} form={el} />
                    ))}
                </div>

                <NotificationComponent notification={notification} />

                <div className="button-group">
                    <div className="button-group-item">
                        <ButtonComponent click={handleSubmit} title="Submit" />
                    </div>
                </div>
            </>
        )
    } else if (loading) {
        return (
            <>
                <p className="emphasize">
                    The payment page will display upon submitting the
                    application. You may submit the fee now or return at a later
                    date, however, applications are not processed until the
                    payment has been received. To make a payment at a later
                    date, please log in to your MQA Online Services Portal
                    account and select “Make Payments” from the “Additional
                    Activities” menu. Use the same login credentials to log in
                    to the MQA Online Services Portal.
                </p>

                <div className="form-structured-list-group">
                    <LoadingComponent />
                </div>

                <NotificationComponent notification={notification} />

                <div className="button-group">
                    <div className="button-group-item">
                        <ButtonComponent click={handleSubmit} title="Submit" />
                    </div>
                </div>
            </>
        )
    } else {
        return (
            <>
                <p className="emphasize">
                    The payment page will display upon submitting the
                    application. You may submit the fee now or return at a later
                    date, however, applications are not processed until the
                    payment has been received. To make a payment at a later
                    date, please log in to your MQA Online Services Portal
                    account and select “Make Payments” from the “Additional
                    Activities” menu. Use the same login credentials to log in
                    to the MQA Online Services Portal.
                </p>

                <div className="form-structured-list-group">
                    <LoadingComponent />
                </div>

                <NotificationComponent notification={notification} />

                <div className="button-group">
                    <div className="button-group-item">
                        <ButtonComponent click={handleSubmit} title="Submit" />
                    </div>
                </div>
            </>
        )
    }
}

export default SummarySubmissionForm
